import {
	ErrorModal,
	useMesfRealtime,
	useUserContext,
} from "@dexteel/mesf-core";
import { Autocomplete, Box, CircularProgress, TextField } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FieldError } from "react-hook-form";

import { Heat } from "../models/Heat";
import { getHeats } from "../repositories/HeatRepository";

type UseHeatsProps = {
	includeInactive?: boolean;
	sectionId?: number;
	assetId?: number;
	refetchInterval?: number;
};

export const useHeats = ({
	includeInactive = false,
	sectionId,
	assetId,
	refetchInterval,
}: UseHeatsProps = {}) => {
	const {
		state: { defaultAreaId },
	} = useUserContext();

	return useQuery<Heat[], Error>({
		queryKey: ["heats", includeInactive, sectionId],
		queryFn: ({ signal }) =>
			getHeats(
				{
					includeInactive,
					sectionId,
					defaultAreaId: assetId || Number(defaultAreaId),
				},
				signal,
			),
		refetchInterval,
	});
};

type Props = {
	label?: string;
	value: number | null;
	onChange?: (value: number | null, heat?: Heat | null) => void;
	disabled?: boolean;
	required?: boolean;
	sectionId?: number;
	assetId?: number;
	fieldError?: FieldError;
};

export const HeatSelector = ({
	label = "Heat",
	value,
	onChange = () => {},
	disabled = false,
	required = false,
	sectionId,
	assetId,
	fieldError,
}: Props) => {
	const queryClient = useQueryClient();
	const [error, setError] = useState("");

	const {
		data: heats,
		isLoading,
		error: e,
		isError,
	} = useHeats({
		refetchInterval: 60_000,
		sectionId,
		assetId,
	});

	const selectedValue = heats?.find((h) => h.HeatId === value) || null;

	useEffect(() => {
		if (isError && e) {
			setError(e.message);
		}
	}, [e, isError]);

	useMesfRealtime({
		onReceiveMessage: async (_author, message) => {
			const clean = message.replaceAll("[", "").replaceAll("]", "");
			if (clean === "M2.GetHeats") {
				await queryClient.invalidateQueries({ queryKey: ["heats"] });
			}
		},
	});

	return (
		<>
			<Autocomplete
				size="small"
				clearOnEscape
				autoHighlight
				options={heats || []}
				getOptionLabel={(option) => option.HeatName}
				disabled={disabled}
				value={selectedValue}
				onChange={(_event, newValue) => {
					onChange(newValue?.HeatId ?? null, newValue ?? null);
				}}
				renderOption={(props, option) => (
					<Box
						{...props}
						component="li"
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "flex-start !important",
							width: "100%",
						}}
					>
						<Box sx={{ width: "100%" }}>{option.HeatName}</Box>
						<Box sx={{ fontSize: "0.8em", color: "grey", width: "100%" }}>
							{`${option.GradeName ? `Grade: ${option.GradeName}` : ""}${
								option.Sections ? ` | Section: ${option.Sections}` : ""
							}`}
						</Box>
					</Box>
				)}
				renderInput={(params) => (
					<TextField
						{...params}
						required={required}
						label={label}
						error={!!fieldError}
						helperText={fieldError?.message}
						margin="none"
						sx={{
							backgroundColor: "white",
						}}
						inputProps={{
							...params.inputProps,
							"data-test-id": "heat-selector",
						}}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										params.InputProps.endAdornment
									)}
								</>
							),
						}}
					/>
				)}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading heats"
			/>
		</>
	);
};
